import { Injectable } from '@angular/core';
import { fromJS, List } from 'immutable';
import { has, isBoolean, isNumber, keys, reduce } from 'lodash';
import {
  ALL_LIST_FILTER,
  IFetchFilterKey,
  ISearchParams,
  SearchParamsEnum,
  SortByEnum,
  SortOrderEnum,
} from '../entity-data/entity-data.data';
import {
  getInContent,
  IS_DIRTY_KEY,
  IS_NEW_KEY,
  setInContent,
} from '../entity-data/entity.model';
import { Settings } from '../settings-data/settings.model';
import { DateTimeService, MOMENT_TIMEZONELESS_DATE_FORMAT } from 'i2g-ng-utils';
import { Expense } from './models/expense.model';
import { ALL_YEARS } from './models/total-per-year-filter.model';
import { UtilsService } from '../shared/services/utils/utils.service';
import { EntityDataBuilder } from '../entity-data/entity-data.builder';
import { EntityTypesEnum } from '../../app.enum';
import { EXPENSE_LIST_SEARCH_PARAMS_KEY } from './expense-data.data';
import { UrlService } from '../shared/services/url/url.service';

@Injectable()
export class ExpenseDataBuilder extends EntityDataBuilder<Expense> {
  model = Expense;
  entityType: EntityTypesEnum = EntityTypesEnum.expense;
  yearFilterKey: string = SearchParamsEnum.tax_year;
  listFilterKeys: IFetchFilterKey = {
    [ALL_LIST_FILTER]: EXPENSE_LIST_SEARCH_PARAMS_KEY,
  };

  constructor(
    private _dateTimeService: DateTimeService,
    private _utilsService: UtilsService,
    urlService: UrlService
  ) {
    super(urlService);
  }

  buildEntity(
    props: any = {},
    settings: Settings,
    isDirty?: boolean,
    isNew?: boolean
  ): Expense {
    const localeKey = Expense.CONTENT_KEYS.LOCALE;
    const currencyCodeKey = Expense.CONTENT_KEYS.CURRENCY_CODE;
    const dateKey = Expense.CONTENT_KEYS.DATE;

    props = props && props.toMap ? props.toMap() : fromJS(props || {});

    const expenseProps = props.withMutations((map) => {
      // Default locale
      if (!getInContent(map, localeKey)) {
        setInContent(map, localeKey, settings.documentLocale);
      }

      // Default currency code
      if (!getInContent(map, currencyCodeKey)) {
        setInContent(map, currencyCodeKey, settings.documentCurrencyCode);
      }

      // Default date
      if (!getInContent(map, dateKey)) {
        setInContent(map, dateKey, this._dateTimeService.getDateString());
      }

      if (isBoolean(isDirty)) {
        map.set(IS_DIRTY_KEY, isDirty);
      }

      if (isBoolean(isNew)) {
        map.set(IS_NEW_KEY, isNew);
      }
    });

    return new Expense(expenseProps);
  }

  buildEntities(array) {
    return List<Expense>(array ? array.map(this.buildEntity.bind(this)) : []);
  }

  buildSortItemsComparatorFn(
    sortBy: SortByEnum,
    sortOrder: SortOrderEnum
  ): (expenseA: Expense, expenseB: Expense) => number {
    const order = sortOrder === SortOrderEnum.asc ? 1 : -1;

    switch (sortBy) {
      case SortByEnum.date:
        return (expenseA: Expense, expenseB: Expense): number => {
          const expenseDateA = this._dateTimeService.getMilliseconds(
            expenseA.date,
            MOMENT_TIMEZONELESS_DATE_FORMAT
          );
          const expenseDateB = this._dateTimeService.getMilliseconds(
            expenseB.date,
            MOMENT_TIMEZONELESS_DATE_FORMAT
          );

          if (expenseDateA < expenseDateB) {
            return order * -1;
          } else if (expenseDateA > expenseDateB) {
            return order;
          } else {
            return 0;
          }
        };
      case SortByEnum.category:
        return (expA: Expense, expB: Expense): number => {
          const categoryA = (expA.category || '').toLowerCase();
          const categoryB = (expB.category || '').toLowerCase();

          if (categoryA < categoryB) {
            return order * -1;
          } else if (categoryA > categoryB) {
            return order;
          } else {
            return 0;
          }
        };
      default:
        return null;
    }
  }

  buildListItemsFilterFn(
    searchParams: ISearchParams,
    listFilter: string,
    settings: Settings
  ): (entity: Expense) => boolean {
    const year = searchParams[this.yearFilterKey];

    if (year && year !== ALL_YEARS) {
      const taxYearStart = settings.companySettings.taxYearStart;

      return (expense: Expense) =>
        this._utilsService.isInTaxYear(taxYearStart, year, expense.date);
    }

    return (expense: Expense) => true;
  }

  buildListFilter(entity: any): string {
    return ALL_LIST_FILTER;
  }

  validateSearchParams(searchParams: ISearchParams, filter: string) {
    const validParams = {
      [SearchParamsEnum.sort_by]: (a) =>
        [
          SortByEnum.category,
          SortByEnum.date,
          SortByEnum.created_date,
        ].includes(a),
      [SearchParamsEnum.sort_order]: (a) =>
        [SortOrderEnum.asc, SortOrderEnum.desc].includes(a),
      [SearchParamsEnum.tax_year]: isNumber,
    };
    const requiredKeys = keys(validParams).every((key) =>
      has(searchParams, key)
    );
    const validValues = reduce(
      searchParams,
      (result, value, key) =>
        result && has(validParams, key) && validParams[key](value),
      true
    );

    return requiredKeys && validValues;
  }
}
